import { Component, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { reloadWithoutCachedShell } from '@/lib/swCache';

/**
 * Catches a lazily-loaded route that fails to render — in practice, a chunk
 * that could not be fetched: offline on a route deliberately left out of the
 * precache, or a stale hashed name after a deploy. Suspense only handles a
 * pending import; a rejected one throws past it and would otherwise unmount
 * the whole app. The boundary sits inside the route element, so the header and
 * bottom navigation stay usable and the user can walk away from the failure.
 */

interface LazyRouteBoundaryProps {
  children: ReactNode;
}

interface LazyRouteBoundaryState {
  hasError: boolean;
}

function LazyRouteError() {
  const { t } = useTranslation();
  // The reload is preceded by a bounded network probe, so the click is not
  // instant — disable the button rather than let it look unresponsive.
  const [isRetrying, setIsRetrying] = useState(false);
  return (
    <div className="space-y-3 py-8 text-center">
      <p className="text-slate-500">{t('app.sectionLoadFailed')}</p>
      <button
        type="button"
        className="btn-secondary h-9 px-4 text-sm"
        disabled={isRetrying}
        // React.lazy caches a rejected import, so re-rendering would replay the
        // same failure — only a fresh document retries the fetch.
        onClick={() => {
          setIsRetrying(true);
          reloadWithoutCachedShell();
        }}
      >
        {t('app.retry')}
      </button>
    </div>
  );
}

export class LazyRouteBoundary extends Component<
  LazyRouteBoundaryProps,
  LazyRouteBoundaryState
> {
  override state: LazyRouteBoundaryState = { hasError: false };

  static getDerivedStateFromError(): LazyRouteBoundaryState {
    return { hasError: true };
  }

  override render(): ReactNode {
    if (this.state.hasError) return <LazyRouteError />;
    return this.props.children;
  }
}
